import { useState } from "react";
import moment from "moment-timezone";
import { Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Combobox } from "@/components/ui/combobox";
import { CommodityOptions } from "@/lib/api/commodity";
import { useListMarketZones } from "@/lib/api/generalContract";
import { useListMerchValues, RiskMerchValue } from "@/lib/api/risk/merchValues";
import { CommonDestinationTimezone } from "@/lib/api/utils";
import { MerchValueCard } from "./MerchValueCard";
import { CreateMerchValueDialog } from "./CreateMerchValueDialog";
import { EditMerchValueDialog } from "./EditMerchValueDialog";
import { DeleteMerchValueDialog } from "./DeleteMerchValueDialog";
import { ArchiveMerchValueDialog } from "./ArchiveMerchValueDialog";

// ---------------------------------------------------------------------------
// Crop year options
// ---------------------------------------------------------------------------

function buildCropYearOptions() {
  const currentYear = new Date().getFullYear();
  return Array.from({ length: 5 }, (_, i) => ({
    value: (currentYear + 1 - i).toString(),
    label: (currentYear + 1 - i).toString(),
  }));
}

const cropYearOptions = buildCropYearOptions();

// ---------------------------------------------------------------------------
// Commodity label lookup
// ---------------------------------------------------------------------------

const commodityLabelMap = new Map<string, string>(
  CommodityOptions.map((o) => [o.value as string, o.label]),
);

function getCommodityLabel(commodityValue: string): string {
  return commodityLabelMap.get(commodityValue) || commodityValue;
}

// ---------------------------------------------------------------------------
// Grouping helpers
// ---------------------------------------------------------------------------

interface MarketZoneGroup {
  zoneName: string;
  items: RiskMerchValue[];
}

interface CommoditySection {
  commodityLabel: string;
  zoneGroups: { zoneId: string; zoneName: string; items: RiskMerchValue[] }[];
}

function sortItemsByRangeStart(items: RiskMerchValue[]) {
  items.sort((a, b) => {
    const aStart = moment.tz(a.range_start, CommonDestinationTimezone).valueOf();
    const bStart = moment.tz(b.range_start, CommonDestinationTimezone).valueOf();
    return aStart - bStart;
  });
}

function buildGroupedByZone(
  items: RiskMerchValue[],
  marketZoneNameMap: Map<string, string>,
): { zoneId: string; zoneName: string; items: RiskMerchValue[] }[] {
  const grouped = new Map<string, MarketZoneGroup>();
  for (const item of items) {
    const zoneId = item.market_zone_id;
    if (!grouped.has(zoneId)) {
      const zoneName =
        item.MarketZone?.dtn_market_zone ||
        marketZoneNameMap.get(zoneId) ||
        "Unknown Zone";
      grouped.set(zoneId, { zoneName, items: [] });
    }
    grouped.get(zoneId)!.items.push(item);
  }

  const sorted = Array.from(grouped.entries())
    .sort((a, b) => a[1].zoneName.localeCompare(b[1].zoneName))
    .map(([zoneId, group]) => ({ zoneId, zoneName: group.zoneName, items: group.items }));

  for (const group of sorted) {
    sortItemsByRangeStart(group.items);
  }

  return sorted;
}

function buildCommoditySections(
  items: RiskMerchValue[],
  marketZoneNameMap: Map<string, string>,
): CommoditySection[] {
  const byCommodity = new Map<string, RiskMerchValue[]>();
  for (const item of items) {
    if (!byCommodity.has(item.commodity)) {
      byCommodity.set(item.commodity, []);
    }
    byCommodity.get(item.commodity)!.push(item);
  }

  return Array.from(byCommodity.entries())
    .sort((a, b) => getCommodityLabel(a[0]).localeCompare(getCommodityLabel(b[0])))
    .map(([commodity, commodityItems]) => ({
      commodityLabel: getCommodityLabel(commodity),
      zoneGroups: buildGroupedByZone(commodityItems, marketZoneNameMap),
    }));
}

// ---------------------------------------------------------------------------
// Active tab
// ---------------------------------------------------------------------------

export function MerchValuesActiveTab() {
  const [cropYear, setCropYear] = useState<number>(new Date().getFullYear());
  const [selectedCommodity, setSelectedCommodity] = useState<string>("");
  const [selectedMarketZone, setSelectedMarketZone] = useState<string>("");

  const [createOpen, setCreateOpen] = useState(false);
  const [editItem, setEditItem] = useState<RiskMerchValue | null>(null);
  const [deleteItem, setDeleteItem] = useState<RiskMerchValue | null>(null);
  const [archiveItem, setArchiveItem] = useState<RiskMerchValue | null>(null);

  // Fetch market zones
  const marketZonesQuery = useListMarketZones();
  const marketZoneOptions = (marketZonesQuery.data?.data || []).map(
    (zone: { id: string; dtn_market_zone: string }) => ({
      value: zone.id,
      label: zone.dtn_market_zone,
    }),
  );

  // Fetch merch values
  const merchValuesQuery = useListMerchValues({
    crop_year: cropYear,
    commodity: selectedCommodity || undefined,
    market_zone_id: selectedMarketZone || undefined,
  });

  const allItems: RiskMerchValue[] = merchValuesQuery.data?.data || [];

  // Build a map of market_zone_id -> zone name
  const marketZoneNameMap = new Map<string, string>(
    (marketZonesQuery.data?.data || []).map(
      (zone: { id: string; dtn_market_zone: string }) => [zone.id, zone.dtn_market_zone],
    ),
  );

  // When a commodity is selected: group by market zone only
  // When no commodity is selected: group by commodity, then by market zone
  const hasCommodityFilter = !!selectedCommodity;
  const zoneOnlyGroups = hasCommodityFilter
    ? buildGroupedByZone(allItems, marketZoneNameMap)
    : [];
  const commoditySections = hasCommodityFilter
    ? []
    : buildCommoditySections(allItems, marketZoneNameMap);

  const hasResults = hasCommodityFilter
    ? zoneOnlyGroups.length > 0
    : commoditySections.length > 0;

  const isLoading = merchValuesQuery.isLoading || marketZonesQuery.isLoading;

  return (
    <div className="flex flex-col gap-4 h-full overflow-auto">
      {/* Filters row */}
      <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-end">
        <Combobox
          options={cropYearOptions}
          value={cropYear.toString()}
          onValueChange={(value) => setCropYear(parseInt(value))}
          placeholder="Select crop year"
          dwimOnChange
        />
        <Combobox
          options={CommodityOptions.map((o) => ({ value: o.value, label: o.label }))}
          value={selectedCommodity}
          onValueChange={setSelectedCommodity}
          placeholder="All commodities"
          dwimOnChange
        />
        <Combobox
          options={marketZoneOptions}
          value={selectedMarketZone}
          onValueChange={setSelectedMarketZone}
          placeholder="All market zones"
          dwimOnChange
        />
        <Button onClick={() => setCreateOpen(true)} className="gap-1">
          <Plus className="h-4 w-4" />
          Add
        </Button>
      </div>

      {/* Content */}
      {isLoading ? (
        <p className="text-muted-foreground">Loading...</p>
      ) : !hasResults ? (
        <p className="text-muted-foreground">No merch values found.</p>
      ) : hasCommodityFilter ? (
        /* Single commodity selected — flat market zone groups */
        <div className="flex flex-col gap-4">
          {zoneOnlyGroups.map((group) => (
            <div key={group.zoneId} className="flex flex-col gap-1.5">
              <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
                {group.zoneName}
              </h3>
              <div className="flex flex-row flex-wrap gap-2">
                {group.items.map((item) => (
                  <MerchValueCard
                    key={item.id}
                    item={item}
                    onClick={setEditItem}
                    onDelete={setDeleteItem}
                    onArchive={setArchiveItem}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : (
        /* No commodity selected — grouped by commodity, then market zone */
        <div className="flex flex-col gap-6">
          {commoditySections.map((section) => (
            <div key={section.commodityLabel} className="flex flex-col gap-3">
              <h2 className="text-base font-bold">{section.commodityLabel}</h2>
              <div className="flex flex-col gap-3 pl-2">
                {section.zoneGroups.map((group) => (
                  <div key={group.zoneId} className="flex flex-col gap-1.5">
                    <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
                      {group.zoneName}
                    </h3>
                    <div className="flex flex-row flex-wrap gap-2">
                      {group.items.map((item) => (
                        <MerchValueCard
                          key={item.id}
                          item={item}
                          onClick={setEditItem}
                          onDelete={setDeleteItem}
                          onArchive={setArchiveItem}
                        />
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create dialog */}
      <CreateMerchValueDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        defaults={{
          crop_year: cropYear,
          commodity: selectedCommodity || undefined,
          market_zone_id: selectedMarketZone || undefined,
        }}
        existingItems={allItems}
      />

      {/* Edit dialog */}
      {editItem && (
        <EditMerchValueDialog
          open={!!editItem}
          onOpenChange={(open) => {
            if (!open) setEditItem(null);
          }}
          merchValue={editItem}
          existingItems={allItems}
        />
      )}

      {/* Delete dialog */}
      {deleteItem && (
        <DeleteMerchValueDialog
          open={!!deleteItem}
          onOpenChange={(open) => {
            if (!open) setDeleteItem(null);
          }}
          merchValueId={deleteItem.id}
        />
      )}

      {/* Archive dialog */}
      {archiveItem && (
        <ArchiveMerchValueDialog
          open={!!archiveItem}
          onOpenChange={(open) => {
            if (!open) setArchiveItem(null);
          }}
          merchValueId={archiveItem.id}
        />
      )}
    </div>
  );
}
